import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, Upload } from "lucide-react";
import { formatApiError } from "@/lib/api";
import { VIEW_LABELS } from "@/components/shirts/ApparelCanvas";

const CHECKER = {
    backgroundImage: "linear-gradient(45deg, #e4e4e7 25%, transparent 25%), linear-gradient(-45deg, #e4e4e7 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e4e4e7 75%), linear-gradient(-45deg, transparent 75%, #e4e4e7 75%)",
    backgroundSize: "16px 16px", backgroundPosition: "0 0, 0 8px, 8px -8px, -8px 0",
};

/** One print location. Collapsed to a checkbox until you turn it on, then it opens the dropzone. */
function ArtSlot({ product, view, cfg, theme, active, defaultOpen }) {
    const art = cfg.arts[view];
    const busy = cfg.busy[view];
    const fileRef = useRef(null);
    const [over, setOver] = useState(false);
    const [open, setOpen] = useState(defaultOpen);
    const label = VIEW_LABELS[view];
    const hasText = (cfg.texts[view] || []).some((t) => t.text?.trim());
    const hasContent = !!(art?.url || busy || hasText);
    const expanded = open || hasContent;

    const pick = async (file) => {
        if (!file) return;
        cfg.setView(view);
        try {
            await cfg.pickArt(view, file);
            toast.success(`${label} image uploaded`);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const toggle = () => {
        if (expanded) {
            if (art?.url) cfg.setArt(view, null);   // turning a side off clears its image
            setOpen(false);
        } else {
            setOpen(true);
            cfg.setView(view);
        }
    };

    return (
        <div className={`rounded-xl border bg-white transition-colors ${active && expanded ? "shadow-sm" : "border-zinc-200"}`}
            style={active && expanded ? { borderColor: theme?.primary } : undefined} data-testid={`art-slot-${view}`}
            onDragOver={(e) => { if (expanded) { e.preventDefault(); setOver(true); } }} onDragLeave={() => setOver(false)}
            onDrop={(e) => { if (expanded) { e.preventDefault(); setOver(false); pick(e.dataTransfer.files?.[0]); } }}>
            <label className="flex w-full cursor-pointer items-center gap-2.5 px-2.5 py-2.5" data-testid={`side-toggle-${view}`}
                onClick={(e) => { if (expanded) { e.preventDefault(); cfg.setView(view); } }}>
                <input type="checkbox" checked={expanded} onChange={toggle} data-testid={`side-check-${view}`}
                    className="h-4 w-4 shrink-0 rounded border-zinc-300"
                    style={{ accentColor: theme?.primary }} />
                <span className="flex-1 text-left font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                    {expanded ? "Add " : "Add "}<span className="font-bold text-zinc-800">{label}</span>
                    {expanded && <span className="ml-1 normal-case tracking-normal text-zinc-400">· scales with each shirt size</span>}
                </span>
                {(active && expanded) || hasText ? <span className="h-1.5 w-1.5 rounded-full" style={{ background: active && expanded ? theme?.primary : "#a1a1aa" }} /> : null}
            </label>

            {expanded && (
                <div className="px-2.5 pb-2.5">
                    {art?.url || busy ? (
                        <>
                            <div className="relative h-24 overflow-hidden rounded-lg border border-zinc-200" style={CHECKER}>
                                {art?.url && <img src={art.url} alt={label} className="h-full w-full object-contain" data-testid={`art-thumb-${view}`} />}
                                {busy && <span className="absolute inset-0 grid place-items-center bg-white/60"><Loader2 className="animate-spin" size={18} style={{ color: theme?.primary }} /></span>}
                            </div>
                            {art?.file && <p className="mt-1.5 truncate text-[11px] text-zinc-700" title={art.file.filename} data-testid={`art-filename-${view}`}>{art.file.filename}</p>}
                            {art?.widthIn && (
                                <p className="text-[10px] leading-snug text-zinc-500" data-testid={`art-size-${view}`}>
                                    Prints in proportion to each shirt size — smaller on youth, bigger on 3XL.
                                </p>
                            )}
                            <div className="mt-1.5 flex gap-3 text-[11px] font-semibold">
                                <button type="button" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1 text-zinc-700 hover:underline" data-testid={`art-replace-${view}`}><Upload size={11} /> Replace</button>
                                <button type="button" onClick={() => cfg.setArt(view, null)} className="inline-flex items-center gap-1 text-zinc-500 hover:text-red-600" data-testid={`art-remove-${view}`}><Trash2 size={11} /> Remove</button>
                            </div>
                        </>
                    ) : (
                        <button type="button" onClick={() => fileRef.current?.click()} data-testid={`art-upload-${view}`}
                            className={`flex w-full flex-col items-center gap-1 rounded-lg border-2 border-dashed px-3 py-4 text-center transition-colors ${over ? "bg-zinc-50" : "border-zinc-300 hover:bg-zinc-50"}`}
                            style={over ? { borderColor: theme?.primary } : undefined}>
                            <Upload size={16} style={{ color: theme?.primary }} />
                            <span className="text-xs font-bold text-zinc-800">Select {label.toLowerCase()} image</span>
                            <span className="text-[11px] text-zinc-500">or drop it here</span>
                        </button>
                    )}
                </div>
            )}
            <input ref={fileRef} type="file" className="hidden" accept="image/*,.pdf,.ai,.eps,.svg,.psd"
                onChange={(e) => { pick(e.target.files[0]); e.target.value = ""; }} data-testid={`art-input-${view}`} />
        </div>
    );
}

/** The left rail: one collapsible slot per side of the garment. */
export default function ArtRail({ product, cfg, theme, perLocation, className = "" }) {
    return (
        <aside className={`flex flex-col gap-2.5 overflow-y-auto bg-white p-3 ${className}`} data-testid="art-rail">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Print locations</p>
            {cfg.views.map((v, i) => <ArtSlot key={v} product={product} view={v} cfg={cfg} theme={theme} active={cfg.view === v} defaultOpen={i === 0} />)}
            <p className="mt-auto text-[11px] leading-snug text-zinc-400">
                Tick a side to add a print there. PNG, JPG, PDF, AI, EPS or SVG. On the garment, click the image to move,
                scale, lock or layer it.
                {perLocation ? ` Every side with an image or text is one print location (${perLocation}).` : ""}
            </p>
        </aside>
    );
}
